import React, { useEffect, useRef } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

import logo from "./RoyalLogo.png";
import schoolQRCode from "./defaultAvatar.png";
import api from "../../api/api";

/* ==========================================================================
   ROYAL PRIESTHOOD ACADEMY
   STUDENT PERIODIC REPORT
   Premium A4 Landscape Academic Report
   Royal Blue • Sky Blue • Light Blue • White
   Preserves existing report data and automatic PDF download
   ========================================================================== */

const SCHOOL_NAME = "Royal Priesthood Academy";
const SCHOOL_SUBTITLE =
  "Daycare, Elementary, Junior & Senior High School";

const SCHOOL_ADDRESS =
  "New Jerusalem Community, Johnsonville, Monrovia, Liberia";

const SCHOOL_CONTACT =
  "Contact: +231-775-912-321 / +231-881-403-326 / +231-777-506-311";

const SYSTEM_NAME = "Polu Integrated School Management System";

const COLORS = {
  royalBlue: "#123F8C",
  royalDeep: "#0A2B61",
  royalMid: "#245CB0",
  skyBlue: "#65B9F2",
  skyLight: "#EAF6FF",
  paleBlue: "#F4F9FE",
  softBlue: "#E0EFFB",
  white: "#FFFFFF",
  page: "#FFFFFF",
  text: "#202A36",
  textSecondary: "#4B5563",
  muted: "#6B7280",
  border: "#D9E4EF",
  borderDark: "#B8CADC",
  green: "#187345",
  greenLight: "#EDF8F1",
  red: "#B42332",
  redLight: "#FCEFF0",
  grayLight: "#F7F9FC",
  gray: "#E9EEF4",
};

/* ==========================================================================
   API BASE
   ========================================================================== */

const API_BASE = api?.defaults?.baseURL || "";

/* ==========================================================================
   PROFESSIONAL PDF STYLES
   ========================================================================== */

const styles = StyleSheet.create({
  /* PAGE */

  page: {
    paddingTop: 17,
    paddingBottom: 37,
    paddingHorizontal: 19,
    backgroundColor: COLORS.page,
    fontFamily: "Helvetica",
    color: COLORS.text,
  },

  reportWrapper: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
  },

  /* STUDENT REPORT CARD */

  card: {
    width: "49.35%",
    border: `0.8 solid ${COLORS.border}`,
    borderRadius: 8,
    paddingTop: 10,
    paddingBottom: 8,
    paddingHorizontal: 10,
    backgroundColor: COLORS.white,
    position: "relative",
    overflow: "hidden",
  },

  cardTopAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: COLORS.royalBlue,
  },

  cardSkyAccent: {
    position: "absolute",
    top: 4,
    left: 0,
    width: "34%",
    height: 2,
    backgroundColor: COLORS.skyBlue,
  },

  watermarkOne: {
    position: "absolute",
    top: 30,
    right: 10,
    width: 65,
    height: 65,
    borderRadius: 33,
    backgroundColor: COLORS.skyBlue,
    opacity: 0.045,
  },

  watermarkTwo: {
    position: "absolute",
    top: 43,
    right: 35,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: COLORS.royalBlue,
    opacity: 0.035,
  },

  watermarkThree: {
    position: "absolute",
    top: 54,
    right: 18,
    width: 23,
    height: 23,
    borderRadius: 12,
    border: `1 solid ${COLORS.skyBlue}`,
    opacity: 0.12,
  },

  /* SCHOOL HEADER */

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 3,
    paddingBottom: 8,
    marginBottom: 7,
    borderBottom: `1 solid ${COLORS.border}`,
  },

  logoFrame: {
    width: 49,
    height: 49,
    flexShrink: 0,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    border: `1.2 solid ${COLORS.softBlue}`,
    padding: 3,
    marginRight: 9,
    alignItems: "center",
    justifyContent: "center",
  },

  logo: {
    width: 41,
    height: 41,
    objectFit: "contain",
  },

  schoolInfo: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    paddingRight: 6,
  },

  schoolName: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    marginBottom: 3,
  },

  schoolSubtitle: {
    fontSize: 6.8,
    fontWeight: "bold",
    color: COLORS.royalMid,
    marginBottom: 3,
  },

  schoolAddress: {
    fontSize: 5.9,
    lineHeight: 1.35,
    color: COLORS.textSecondary,
    marginBottom: 1.5,
  },

  schoolReportBadge: {
    alignSelf: "flex-start",
    backgroundColor: COLORS.skyLight,
    border: `0.7 solid ${COLORS.softBlue}`,
    borderRadius: 3,
    paddingVertical: 3,
    paddingHorizontal: 7,
    marginTop: 4,
  },

  reportTitle: {
    color: COLORS.royalDeep,
    fontSize: 6.5,
    fontWeight: "bold",
    letterSpacing: 0.65,
  },

  /* STUDENT PHOTO */

  admissionPhotoColumn: {
    width: 59,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },

  admissionPhotoFrame: {
    width: 51,
    height: 57,
    borderRadius: 5,
    border: `1.2 solid ${COLORS.skyBlue}`,
    padding: 2,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },

  admissionPhoto: {
    width: 45,
    height: 51,
    objectFit: "cover",
    borderRadius: 2,
  },

  photoPlaceholder: {
    width: 45,
    height: 51,
    backgroundColor: COLORS.skyLight,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
  },

  photoPlaceholderText: {
    fontSize: 5.5,
    fontWeight: "bold",
    textAlign: "center",
    color: COLORS.royalBlue,
  },

  photoCaption: {
    marginTop: 3,
    fontSize: 5,
    color: COLORS.muted,
    textAlign: "center",
  },

  /* SECTION HEADINGS */

  sectionHeading: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 2,
    marginBottom: 5,
  },

  sectionAccent: {
    width: 3,
    height: 11,
    backgroundColor: COLORS.skyBlue,
    borderRadius: 2,
    marginRight: 6,
  },

  sectionHeadingText: {
    fontSize: 7.6,
    color: COLORS.royalDeep,
    fontWeight: "bold",
    letterSpacing: 0.55,
  },

  /* STUDENT INFORMATION */

  infoBox: {
    flexDirection: "row",
    alignItems: "stretch",
    backgroundColor: COLORS.paleBlue,
    border: `0.8 solid ${COLORS.border}`,
    borderRadius: 5,
    paddingVertical: 7,
    paddingHorizontal: 8,
    marginBottom: 7,
  },

  studentDetails: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingRight: 8,
    justifyContent: "center",
  },

  infoRow: {
    flexDirection: "row",
    marginBottom: 4,
    alignItems: "flex-start",
  },

  infoRowLast: {
    marginBottom: 0,
  },

  label: {
    width: 76,
    flexShrink: 0,
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.textSecondary,
  },

  value: {
    flexGrow: 1,
    flexShrink: 1,
    fontSize: 7.4,
    color: COLORS.text,
    fontWeight: "bold",
  },

  studentNameValue: {
    color: COLORS.royalDeep,
    fontSize: 8.5,
  },

  /* QR CODE */

  qrColumn: {
    width: 65,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderLeft: `0.8 solid ${COLORS.border}`,
    paddingLeft: 7,
  },

  qrFrame: {
    width: 47,
    height: 47,
    padding: 3,
    borderRadius: 4,
    border: `0.8 solid ${COLORS.softBlue}`,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
  },

  qrImage: {
    width: 39,
    height: 39,
    objectFit: "contain",
  },

  qrCaption: {
    marginTop: 3,
    fontSize: 4.8,
    fontWeight: "bold",
    color: COLORS.royalBlue,
    textAlign: "center",
  },

  /* SUBJECT TABLE */

  table: {
    width: "100%",
    border: `0.8 solid ${COLORS.border}`,
    borderRadius: 5,
    overflow: "hidden",
    marginTop: 1,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.royalBlue,
    paddingVertical: 6,
    borderBottom: `2 solid ${COLORS.skyBlue}`,
  },

  headerSubject: {
    width: "74%",
    textAlign: "left",
    fontSize: 7,
    fontWeight: "bold",
    paddingLeft: 8,
    color: COLORS.white,
    letterSpacing: 0.45,
  },

  headerScore: {
    width: "26%",
    textAlign: "center",
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.white,
    letterSpacing: 0.45,
  },

  tableRow: {
    flexDirection: "row",
    minHeight: 18,
    alignItems: "center",
    borderBottom: `0.55 solid ${COLORS.border}`,
  },

  tableRowAlternate: {
    backgroundColor: COLORS.paleBlue,
  },

  subjectCell: {
    width: "74%",
    paddingLeft: 8,
    paddingRight: 5,
    paddingVertical: 4,
    fontSize: 7.4,
    color: COLORS.text,
  },

  scoreCell: {
    width: "26%",
    textAlign: "center",
    fontSize: 7.5,
    fontWeight: "bold",
    paddingVertical: 4,
    borderLeft: `0.6 solid ${COLORS.border}`,
  },

  scoreGood: {
    color: COLORS.green,
  },

  scoreBad: {
    color: COLORS.red,
  },

  scoreNeutral: {
    color: COLORS.royalBlue,
  },

  emptySubjects: {
    width: "100%",
    textAlign: "center",
    fontSize: 7.5,
    paddingVertical: 12,
    color: COLORS.textSecondary,
  },

  /* PERFORMANCE SUMMARY */

  summaryBox: {
    marginTop: 7,
    paddingVertical: 7,
    paddingHorizontal: 8,
    backgroundColor: COLORS.paleBlue,
    border: `0.8 solid ${COLORS.border}`,
    borderLeft: `2.5 solid ${COLORS.royalBlue}`,
    borderRadius: 5,
  },

  summaryHeading: {
    fontSize: 7.3,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    marginBottom: 6,
    letterSpacing: 0.35,
  },

  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 5,
  },

  summaryRowLast: {
    marginBottom: 0,
  },

  summaryLabel: {
    flex: 1,
    fontSize: 7,
    color: COLORS.textSecondary,
    fontWeight: "bold",
  },

  summaryValue: {
    flex: 1,
    fontSize: 7.5,
    color: COLORS.text,
    fontWeight: "bold",
    textAlign: "right",
  },

  averagePill: {
    backgroundColor: COLORS.white,
    border: `0.8 solid ${COLORS.softBlue}`,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 7,
    alignSelf: "flex-end",
  },

  averageValue: {
    fontSize: 8.5,
    color: COLORS.royalBlue,
    fontWeight: "bold",
  },

  remarkValue: {
    color: COLORS.green,
  },

  /* SIGNATURES */

  signatures: {
    marginTop: 13,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  signatureBox: {
    width: "43%",
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    borderTop: `0.9 solid ${COLORS.borderDark}`,
    marginBottom: 4,
  },

  signatureText: {
    fontSize: 7.2,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    textAlign: "center",
  },

  signatureSubtext: {
    fontSize: 6,
    color: COLORS.muted,
    textAlign: "center",
    marginTop: 2,
  },

  /* REPORT FOOTER */

  cardFooter: {
    marginTop: 9,
    paddingTop: 5,
    borderTop: `0.7 solid ${COLORS.border}`,
    alignItems: "center",
  },

  cardFooterText: {
    fontSize: 5.7,
    color: COLORS.muted,
    textAlign: "center",
  },

  pageFooter: {
    position: "absolute",
    left: 19,
    right: 19,
    bottom: 12,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `0.8 solid ${COLORS.border}`,
    paddingTop: 5,
  },

  pageFooterLeft: {
    fontSize: 6.2,
    color: COLORS.textSecondary,
  },

  pageFooterRight: {
    fontSize: 6.5,
    color: COLORS.royalBlue,
    fontWeight: "bold",
  },

  emptyCard: {
    width: "49.35%",
  },
});

/* ==========================================================================
   SCORE HELPERS
   ========================================================================== */

const isLowScore = (score) => {
  if (typeof score === "number") {
    return score < 70;
  }

  if (typeof score === "string") {
    const normalized = score.trim().toUpperCase();

    if (["D", "F"].includes(normalized)) {
      return true;
    }

    const numericScore = Number(normalized);

    if (normalized !== "" && Number.isFinite(numericScore)) {
      return numericScore < 70;
    }
  }

  return false;
};

const getScoreStyle = (score) => {
  if (score === null || score === undefined || score === "") {
    return styles.scoreNeutral;
  }

  return isLowScore(score)
    ? styles.scoreBad
    : styles.scoreGood;
};

const groupReports = (reports = []) => {
  const pages = [];

  for (let i = 0; i < reports.length; i += 2) {
    pages.push(reports.slice(i, i + 2));
  }

  return pages;
};

const displayValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
};

/* ==========================================================================
   ADMISSION PHOTO URL HELPERS
   ========================================================================== */

const getImageString = (value) => {
  if (typeof value === "string") {
    const result = value.trim();
    return result || null;
  }

  if (value && typeof value === "object") {
    if (typeof value.url === "string" && value.url.trim()) {
      return value.url.trim();
    }

    if (typeof value.uri === "string" && value.uri.trim()) {
      return value.uri.trim();
    }
  }

  return null;
};

const getAdmissionImage = (report) => {
  const candidates = [
    report?.student_admission_image_url,
    report?.student_admission_image,
    report?.admission_image_url,
    report?.admission_image,

    report?.student?.student_admission_image_url,
    report?.student?.student_admission_image,
    report?.student?.admission_image_url,
    report?.student?.admission_image,

    report?.student_admission?.student_admission_image_url,
    report?.student_admission?.student_admission_image,
    report?.student_admission?.admission_image_url,
    report?.student_admission?.admission_image,

    report?.admission?.student_admission_image_url,
    report?.admission?.student_admission_image,
    report?.admission?.admission_image_url,
    report?.admission?.admission_image,

    report?.student?.admission?.student_admission_image_url,
    report?.student?.admission?.student_admission_image,
  ];

  for (const candidate of candidates) {
    const image = getImageString(candidate);

    if (image) {
      return makeAbsoluteMediaUrl(image);
    }
  }

  return null;
};

const makeAbsoluteMediaUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") {
    return null;
  }

  const value = imageUrl.trim();

  if (!value) {
    return null;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (/^data:image\//i.test(value)) {
    return value;
  }

  if (value.startsWith("//")) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${value}`;
    }

    return `https:${value}`;
  }

  if (API_BASE) {
    try {
      const apiUrl =
        typeof window !== "undefined"
          ? new URL(API_BASE, window.location.origin)
          : new URL(API_BASE);

      const origin = apiUrl.origin;

      if (value.startsWith("/")) {
        return `${origin}${value}`;
      }

      return `${origin}/${value}`;
    } catch (error) {
      console.warn(
        "Could not resolve API base URL for student photo."
      );
    }
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    try {
      return new URL(value, window.location.origin).href;
    } catch (error) {
      console.warn(
        "Could not resolve student admission photo URL."
      );
    }
  }

  return value;
};

/* ==========================================================================
   STUDENT PERIODIC REPORT CARD
   ========================================================================== */

const StudentGradeCard = ({ report }) => {
  if (!report) {
    return <View style={styles.emptyCard} />;
  }

  const subjects = Array.isArray(report?.student_subjects)
    ? report.student_subjects
    : [];

  const admissionImage = getAdmissionImage(report);

  const studentName =
    report.student_name ||
    report.full_name ||
    report.student?.full_name ||
    report.student?.student_name;

  const gradeClassName =
    report.grade_class_name ||
    report.grade_class?.name;

  const periodName =
    report.period_name ||
    report.period?.name;

  const academicYearName =
    report.academic_year?.name ||
    report.academic_year_name ||
    (typeof report.academic_year === "string"
      ? report.academic_year
      : undefined);

  return (
    <View style={styles.card} wrap={false}>
      {/* LIGHT DECORATIVE WATERMARKS */}

      <View style={styles.watermarkOne} />
      <View style={styles.watermarkTwo} />
      <View style={styles.watermarkThree} />

      <View style={styles.cardTopAccent} />
      <View style={styles.cardSkyAccent} />

      {/* SCHOOL BRANDING */}

      <View style={styles.header}>
        <View style={styles.logoFrame}>
          <Image src={logo} style={styles.logo} />
        </View>

        <View style={styles.schoolInfo}>
          <Text style={styles.schoolName}>
            {SCHOOL_NAME}
          </Text>

          <Text style={styles.schoolSubtitle}>
            {SCHOOL_SUBTITLE}
          </Text>

          <Text style={styles.schoolAddress}>
            {SCHOOL_ADDRESS}
          </Text>

          <Text style={styles.schoolAddress}>
            {SCHOOL_CONTACT}
          </Text>

          <View style={styles.schoolReportBadge}>
            <Text style={styles.reportTitle}>
              STUDENT PERIODIC REPORT
            </Text>
          </View>
        </View>

        <View style={styles.admissionPhotoColumn}>
          <View style={styles.admissionPhotoFrame}>
            {admissionImage ? (
              <Image
                src={admissionImage}
                style={styles.admissionPhoto}
              />
            ) : (
              <View style={styles.photoPlaceholder}>
                <Text style={styles.photoPlaceholderText}>
                  {"STUDENT\nPHOTO"}
                </Text>
              </View>
            )}
          </View>

          <Text style={styles.photoCaption}>
            STUDENT PHOTO
          </Text>
        </View>
      </View>

      {/* STUDENT INFORMATION */}

      <View style={styles.sectionHeading}>
        <View style={styles.sectionAccent} />

        <Text style={styles.sectionHeadingText}>
          STUDENT INFORMATION
        </Text>
      </View>

      <View style={styles.infoBox}>
        <View style={styles.studentDetails}>
          <View style={styles.infoRow}>
            <Text style={styles.label}>
              Student Name
            </Text>

            <Text style={[styles.value, styles.studentNameValue]}>
              {displayValue(studentName)}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.label}>
              Grade / Class
            </Text>

            <Text style={styles.value}>
              {displayValue(gradeClassName)}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.label}>
              Academic Period
            </Text>

            <Text style={styles.value}>
              {displayValue(periodName)}
            </Text>
          </View>

          <View style={[styles.infoRow, styles.infoRowLast]}>
            <Text style={styles.label}>
              Academic Year
            </Text>

            <Text style={styles.value}>
              {displayValue(academicYearName)}
            </Text>
          </View>
        </View>

        <View style={styles.qrColumn}>
          <View style={styles.qrFrame}>
            <Image
              src={schoolQRCode}
              style={styles.qrImage}
            />
          </View>

          <Text style={styles.qrCaption}>
            SCHOOL QR CODE
          </Text>
        </View>
      </View>

      {/* ACADEMIC PERFORMANCE */}

      <View style={styles.sectionHeading}>
        <View style={styles.sectionAccent} />

        <Text style={styles.sectionHeadingText}>
          ACADEMIC PERFORMANCE
        </Text>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.headerSubject}>
            SUBJECTS
          </Text>

          <Text style={styles.headerScore}>
            SCORE
          </Text>
        </View>

        {subjects.length > 0 ? (
          subjects.map((subject, index) => (
            <View
              key={`${subject.subject_name || "subject"}-${index}`}
              style={[
                styles.tableRow,
                index % 2 === 1 && styles.tableRowAlternate,
              ]}
              wrap={false}
            >
              <Text style={styles.subjectCell}>
                {displayValue(
                  subject.subject_name,
                  "Unnamed Subject"
                )}
              </Text>

              <Text
                style={[
                  styles.scoreCell,
                  getScoreStyle(subject.formatted_final_score),
                ]}
              >
                {displayValue(subject.formatted_final_score)}
              </Text>
            </View>
          ))
        ) : (
          <View style={styles.tableRow}>
            <Text style={styles.emptySubjects}>
              No subject records are available for this report.
            </Text>
          </View>
        )}
      </View>

      {/* PERFORMANCE SUMMARY */}

      <View style={styles.summaryBox} wrap={false}>
        <Text style={styles.summaryHeading}>
          PERFORMANCE SUMMARY
        </Text>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>
            Average Score
          </Text>

          <View style={styles.averagePill}>
            <Text
              style={[
                styles.averageValue,
                getScoreStyle(report.average_score),
              ]}
            >
              {displayValue(report.average_score)}
            </Text>
          </View>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>
            Teacher's Remark
          </Text>

          <Text
            style={[
              styles.summaryValue,
              styles.remarkValue,
            ]}
          >
            {displayValue(report.remark)}
          </Text>
        </View>

        <View style={[styles.summaryRow, styles.summaryRowLast]}>
          <Text style={styles.summaryLabel}>
            Class Rank
          </Text>

          <Text style={styles.summaryValue}>
            {displayValue(report.rank)}
          </Text>
        </View>
      </View>

      {/* SIGNATURES */}

      <View style={styles.signatures} wrap={false}>
        <View style={styles.signatureBox}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Class Teacher
          </Text>

          <Text style={styles.signatureSubtext}>
            Signature / Date
          </Text>
        </View>

        <View style={styles.signatureBox}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Principal
          </Text>

          <Text style={styles.signatureSubtext}>
            Signature / Date
          </Text>
        </View>
      </View>

      {/* CARD FOOTER */}

      <View style={styles.cardFooter}>
        <Text style={styles.cardFooterText}>
          {SCHOOL_NAME} • Generated by {SYSTEM_NAME}
        </Text>
      </View>
    </View>
  );
};

/* ==========================================================================
   CLASS REPORT PDF DOCUMENT
   A4 LANDSCAPE • TWO STUDENTS PER PAGE
   ========================================================================== */

export const ClassReportDocument = ({
  reports = [],
  gradeClass,
  period,
}) => {
  const pages = groupReports(reports);

  return (
    <Document
      title={`${SCHOOL_NAME} - ${gradeClass?.name || ""} ${
        period?.name || ""
      } Report`}
      author={SCHOOL_NAME}
      subject="Student Periodic Grade Report"
      creator={SYSTEM_NAME}
      producer="React PDF"
    >
      {pages.map((pageReports, pageIndex) => (
        <Page
          key={pageIndex}
          size="A4"
          orientation="landscape"
          style={styles.page}
        >
          <View style={styles.reportWrapper}>
            <StudentGradeCard report={pageReports[0]} />

            {pageReports[1] ? (
              <StudentGradeCard report={pageReports[1]} />
            ) : (
              <View style={styles.emptyCard} />
            )}
          </View>

          <View fixed style={styles.pageFooter}>
            <Text style={styles.pageFooterLeft}>
              {SCHOOL_NAME} • {SYSTEM_NAME}
            </Text>

            <Text
              style={styles.pageFooterRight}
              render={({ pageNumber, totalPages }) =>
                `PAGE ${pageNumber} OF ${totalPages}`
              }
            />
          </View>
        </Page>
      ))}
    </Document>
  );
};

/* ==========================================================================
   AUTOMATIC PDF DOWNLOAD
   ========================================================================== */

const ClassReportPDF = ({
  reports,
  gradeClass,
  period,
  onDownloaded,
  onError,
}) => {
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!reports?.length) {
      hasStartedRef.current = false;
      return undefined;
    }

    if (hasStartedRef.current) {
      return undefined;
    }

    hasStartedRef.current = true;

    let isCancelled = false;
    let downloadUrl = null;

    const generateAndDownload = async () => {
      try {
        const blob = await pdf(
          <ClassReportDocument
            reports={reports}
            gradeClass={gradeClass}
            period={period}
          />
        ).toBlob();

        if (isCancelled) {
          return;
        }

        downloadUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = downloadUrl;

        link.download = [
          "Royal_Priesthood_Academy",
          gradeClass?.name || "Class",
          period?.name || "Report",
        ]
          .join("_")
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-") + ".pdf";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        onDownloaded?.();
      } catch (error) {
        if (!isCancelled) {
          console.error("PDF generation failed:", error);
          onError?.(error);
        }
      } finally {
        if (downloadUrl) {
          URL.revokeObjectURL(downloadUrl);
        }
      }
    };

    generateAndDownload();

    return () => {
      isCancelled = true;
    };
  }, [
    reports,
    gradeClass,
    period,
    onDownloaded,
    onError,
  ]);

  return null;
};

export default ClassReportPDF;